import { Chip } from "@heroui/react";

/**
 * Shared chip-row primitive for echoing a small list of summary tags
 * (e.g. funnel answers in `ValidationScreen`, match attributes in
 * `CharacterRevealGate`). Both call sites previously inlined nearly
 * identical markup — same layout, same `Chip` props, same fade-in-up
 * animation — drifting only in `aria-label` and stagger timing. Lifting
 * those into props avoids the duplication while keeping each caller's
 * accessibility label and motion budget intact.
 *
 * Honours `prefers-reduced-motion` (animation collapses to none).
 */
export type SummaryChipsProps = {
  chips: string[];
  /** Whether the user has requested reduced motion. */
  prefersReduced: boolean;
  /** Stagger delay between successive chips, in ms. Defaults to 80. */
  staggerMs?: number;
  /** aria-label for the underlying `<ul>`. Defaults to "Summary". */
  ariaLabel?: string;
  className?: string;
};

export const SummaryChips = ({
  chips,
  prefersReduced,
  staggerMs = 80,
  ariaLabel = "Summary",
  className,
}: SummaryChipsProps) => (
  <ul
    aria-label={ariaLabel}
    className={className ?? "flex max-w-md flex-wrap items-center justify-center gap-2"}
  >
    {chips.map((chip, index) => (
      <li
        key={`${chip}-${index}`}
        style={{
          animation: prefersReduced
            ? undefined
            : `fc-fade-in-up 320ms ${index * staggerMs}ms cubic-bezier(0.32,0.72,0,1) backwards`,
        }}
      >
        <Chip color="accent" size="sm" variant="soft">
          {chip}
        </Chip>
      </li>
    ))}
  </ul>
);

export default SummaryChips;
